import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { usersApi, type AllowlistUser, type UserRole } from '../../lib/usersApi';

/**
 * Admin allowlist editor. Login is Google @riskcherry.com AND this list
 * (superadmins in env always get in). Testers cannot open this page.
 */
export function UsersPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState<AllowlistUser[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>('tester');

  const refresh = async () => {
    const next = await usersApi.list();
    setRows(next);
  };

  useEffect(() => {
    if (user?.role !== 'admin') return;
    void (async () => {
      try {
        await refresh();
      } catch (err) {
        setLoadError((err as Error).message);
      }
    })();
  }, [user?.role]);

  if (user && user.role !== 'admin') {
    return <Navigate to="/settings" replace />;
  }

  const add = async () => {
    setBusy(true);
    setSaveError(null);
    try {
      await usersApi.upsert({
        email: email.trim(),
        name: name.trim() || undefined,
        role,
        active: true,
      });
      setEmail('');
      setName('');
      setRole('tester');
      await refresh();
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const setNameFor = async (row: AllowlistUser, nextName: string) => {
    const trimmed = nextName.trim();
    if (trimmed === (row.name || '').trim()) return;
    setBusy(true);
    setSaveError(null);
    try {
      await usersApi.upsert({
        email: row.email,
        name: trimmed,
        role: row.role,
        active: row.active,
      });
      await refresh();
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const setActive = async (row: AllowlistUser, active: boolean) => {
    setBusy(true);
    setSaveError(null);
    try {
      await usersApi.upsert({ email: row.email, name: row.name, role: row.role, active });
      await refresh();
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const setRoleFor = async (row: AllowlistUser, next: UserRole) => {
    setBusy(true);
    setSaveError(null);
    try {
      await usersApi.upsert({ email: row.email, name: row.name, role: next, active: row.active });
      await refresh();
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: AllowlistUser) => {
    if (!window.confirm(`Remove ${row.email} from the allowlist?`)) return;
    setBusy(true);
    setSaveError(null);
    try {
      await usersApi.remove(row.email);
      await refresh();
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex-col" style={{ gap: 14 }}>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>Users</h1>
        <span className="spacer" />
        <Link to="/settings" className="btn small">
          Back to Settings
        </Link>
      </div>

      <p className="muted small" style={{ marginTop: 0 }}>
        Colleagues sign in with Google on the <code>@riskcherry.com</code> account
        listed here. There is no extra password. Superadmins (env) cannot be
        deleted and always have admin access.
      </p>

      {loadError && (
        <div className="card" style={{ borderColor: 'var(--bad)' }}>
          {loadError}
        </div>
      )}
      {saveError && (
        <p style={{ color: 'var(--bad)', margin: 0 }}>{saveError}</p>
      )}

      <div className="card">
        <h3>Add colleague</h3>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@riskcherry.com"
            style={{ minWidth: 240, flex: 1 }}
          />
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Display name (optional)"
            style={{ minWidth: 160 }}
          />
          <select value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
            <option value="tester">tester</option>
            <option value="admin">admin</option>
          </select>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !email.trim()}
            onClick={() => void add()}
          >
            Add
          </button>
        </div>
      </div>

      <div className="card">
        <h3>Allowlist</h3>
        {rows.length === 0 ? (
          <p className="muted small" style={{ margin: 0 }}>
            No users loaded.
          </p>
        ) : (
          <div className="flex-col" style={{ gap: 8 }}>
            {rows.map((row) => (
              <div key={row.email} className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span className="mono small" style={{ minWidth: 220 }}>
                  {row.email}
                </span>
                <NameField
                  key={`${row.email}:${row.name}`}
                  initial={row.name || ''}
                  disabled={busy}
                  onSave={(next) => void setNameFor(row, next)}
                />
                {row.superadmin ? (
                  <span className="badge small info">superadmin</span>
                ) : (
                  <select
                    value={row.role}
                    disabled={busy}
                    onChange={(e) => void setRoleFor(row, e.target.value as UserRole)}
                  >
                    <option value="tester">tester</option>
                    <option value="admin">admin</option>
                  </select>
                )}
                <span className={`badge small ${row.active ? 'good' : 'grey'}`}>
                  {row.active ? 'active' : 'inactive'}
                </span>
                <span className="spacer" />
                {!row.superadmin && (
                  <>
                    <button
                      type="button"
                      className="btn small"
                      disabled={busy}
                      onClick={() => void setActive(row, !row.active)}
                    >
                      {row.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      type="button"
                      className="btn small btn-danger"
                      disabled={busy}
                      onClick={() => void remove(row)}
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NameField({
  initial,
  disabled,
  onSave,
}: {
  initial: string;
  disabled: boolean;
  onSave: (next: string) => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <input
      className="small"
      value={value}
      disabled={disabled}
      aria-label="Display name"
      style={{ minWidth: 140, maxWidth: 220 }}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => onSave(value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}
